import React, { useLayoutEffect, useRef } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowAltCircleDown,
  faCircleQuestion,
} from "@fortawesome/free-regular-svg-icons";
import { Dropdown, OverlayTrigger, Tooltip } from "react-bootstrap";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import {
  selectIsFacilitator,
  setHandbookContentHeight,
} from "../../../app/appSlice";
import {
  faArrowLeft,
  faBackward,
  faChevronCircleDown,
  faForward,
  faHome,
} from "@fortawesome/free-solid-svg-icons";
import clsx from "clsx";

type Props = {
  title: string;
  subtitle: string;
  page?: number;
  totalPages?: number;
  goBack?: () => void;
  progressCrumbs?: { color: string; name: string; isActive: boolean }[];
  color?: string;
  setPrevPage: any;
  setNextPage: any;
  isLastPage: any;
  isFirstPage: any;
  backToMenu: any;
  resetProgress: any;
};

const Header = ({
  title,
  subtitle,
  progressCrumbs,
  color,
  setPrevPage,
  setNextPage,
  isLastPage,
  isFirstPage,
  backToMenu,
}: Props) => {
  const dispatch = useAppDispatch();
  const headerRef = useRef<any>();
  const isFacilitator = useAppSelector(selectIsFacilitator);

  useLayoutEffect(() => {
    const handleResize = () => {
      const boundingRect = headerRef.current?.getBoundingClientRect();
      boundingRect &&
        dispatch(
          setHandbookContentHeight(
            Math.floor(
              window.innerHeight - (boundingRect.y + boundingRect.height)
            )
          )
        );
    };
    handleResize();
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [headerRef]);
  return (
    <div ref={headerRef} className="d-flex flex-column sidebar-header gap-4">
      <div className="d-flex flex-column-fluid flex-center gap-4 sidebar-header-content">
        {/* Left Section - Titles */}
        {isFacilitator && (
          <>
            <OverlayTrigger
              placement="top"
              overlay={
                <Tooltip className="tooltip-dark">Back to Main Menu</Tooltip>
              }
            >
              <div className={clsx("sidebar-header-menu")} onClick={backToMenu}>
                <FontAwesomeIcon icon={faHome} />
              </div>
            </OverlayTrigger>
          </>
        )}
        <div className="flex-grow-1">
          <div className="fw-bold fs-7">{title}</div>
          <div
            className="fw-bolder fs-5"
            style={{ color, filter: "brightness(0.65)" }}
          >
            {subtitle}
          </div>
        </div>
        {/* Right Section - Controls */}
        {isFacilitator && ( // todo: replace with forward backward buttons
          <div className="d-flex gap-2 quick-controls">
            <OverlayTrigger
              placement="top"
              overlay={
                <Tooltip className="tooltip-dark">
                  {isFirstPage ? "No Previous Page" : "Previous Page"}
                </Tooltip>
              }
            >
              <div
                className={clsx(
                  "sidebar-header-menu",
                  isFirstPage && "opacity-20"
                )}
                onClick={() => {
                  if (!isFirstPage) setPrevPage();
                }}
              >
                <FontAwesomeIcon icon={faBackward} />
              </div>
            </OverlayTrigger>

            <OverlayTrigger
              placement="top"
              overlay={
                <Tooltip className="tooltip-dark">
                  {isLastPage ? "No Next Page" : "Next Page"}
                </Tooltip>
              }
            >
              <div
                className={clsx(
                  "sidebar-header-menu",
                  isLastPage && "opacity-20"
                )}
                onClick={() => {
                  if (!isLastPage) setNextPage();
                }}
              >
                <FontAwesomeIcon icon={faForward} />
              </div>
            </OverlayTrigger>
          </div>
        )}
      </div>
      {progressCrumbs && (
        <div className="d-flex flex-row">
          {progressCrumbs.map((p, i) => (
            <OverlayTrigger
              key={i}
              placement={"bottom"}
              overlay={
                <Tooltip id={`tooltip-bottom`} className="tooltip-dark">
                  {p.name}
                </Tooltip>
              }
            >
              <div
                key={i}
                className="header-chapter-crumb"
                style={{ background: p.color, opacity: p.isActive ? 1 : 0.5 }}
              />
            </OverlayTrigger>
          ))}
        </div>
      )}
    </div>
  );
};

export default Header;
